/**
 * @fileoverview Global Directives Plugin for Vue.js
 * 
 * This module provides a Vue.js plugin that registers custom directives globally
 * throughout the application. These directives extend Vue's template functionality
 * with reusable DOM manipulation and event handling logic.
 * 
 * @module GlobalDirectives
 * @requires vue
 * @requires @/directives/click-ouside.js
 * @requires @/directives/auto-zero.js
 * @requires @/directives/decimal-input.js
 * @author RhomTrip Development Team
 */

import clickOutside from '@/directives/click-ouside.js';
import autoZero from "@/directives/auto-zero.js";
import NumberDirective from '@/directives/v-number.js';

/**
 * Global Directives Plugin Object
 * 
 * A Vue.js plugin that registers custom directives globally across the application.
 * This eliminates the need to import and register directives individually in each component.
 *
 * Register this plugin in your main file with `Vue.use(GlobalDirectives)` to
 * make the directives available in all components without individual imports.
 *
 * @namespace GlobalDirectives
 * @type {Object}
 * @property {Function} install - The plugin installation function called by Vue.use()
 * 
 * @description
 * Provided directives:
 * - `v-click-outside` : Triggers a handler when a click occurs outside the bound element
 * - `v-auto-zero` : Converts empty/whitespace-only input values to numeric zero (0)
 * - `v-decimal` : Provides decimal input validation and formatting functionality
 * 
 * @example
 * // Usage in main.js
 * import Vue from 'vue'
 * import GlobalDirectives from '@/plugins/globalDirectives'
 * Vue.use(GlobalDirectives)
 * 
 * @example
 * // Usage in templates after plugin installation:
 * <div v-click-outside="handleClickOutside">Click outside to close</div>
 * <input v-auto-zero v-model="value" type="number">
 * <input v-decimal v-model="price" placeholder="Enter price">
 */
const GlobalDirectives = {
  /**
   * Plugin installation function
   * 
   * This function is automatically called when the plugin is installed via Vue.use().
   * It registers all imported custom directives as global directives on the Vue instance.
   *
   * @param {import('vue').VueConstructor} Vue - The Vue constructor instance (passed automatically by Vue.use)
   * @returns {void}
   * 
   * @description
   * Registers the following directives:
   * 
   * 1. **click-outside**: Detects clicks outside of an element
   *    - Useful for closing dropdowns, modals, or tooltips
   *    - Binds to a handler function that executes when outside clicks occur
   * 
   * 2. **auto-zero**: Automatically converts empty inputs to zero
   *    - Prevents empty string values in numeric inputs
   *    - Converts whitespace-only values to numeric 0
   *    - Useful for maintaining consistent numeric data types
   * 
   * 3. **v-integer**: Provides integer-only input validation
   *    - Filters non-numeric characters, allowing only digits 0-9
   *    - Ensures input contains only whole numbers
   *    - Enhances user experience for integer inputs
   * 
   * @example
   * // This function is called automatically when using:
   * Vue.use(GlobalDirectives)
   * 
   * @example
   * // After installation, directives are available globally:
   * // v-click-outside usage
   * <div v-click-outside="closeDropdown" class="dropdown">
   *   <button @click="toggleDropdown">Toggle</button>
   *   <ul v-show="isOpen" class="dropdown-menu">...</ul>
   * </div>
   * 
   * // v-auto-zero usage  
   * <input v-auto-zero v-model="quantity" type="number" min="0">
   * 
   * // v-integer usage
   * <input v-integer v-model="count" placeholder="Enter integer">
   */
  install(Vue) {
    // Register click-outside directive for detecting external clicks
    Vue.directive('click-outside', clickOutside);

    // Register auto-zero directive for automatic zero conversion
    Vue.directive('auto-zero', autoZero);

    // Register number-input directive for number validation
    Vue.directive('number', NumberDirective);

  }
};

/**
 * Export the Global Directives Plugin
 * 
 * @exports GlobalDirectives
 * @default GlobalDirectives
 * @description
 * Default export of the GlobalDirectives plugin object.
 * This can be imported and used with Vue.use() to register all custom directives globally.
 * 
 * @example
 * import GlobalDirectives from '@/plugins/globalDirectives'
 * Vue.use(GlobalDirectives)
 * 
 * @example
 * // Alternative import syntax
 * import { install } from '@/plugins/globalDirectives'
 * Vue.use({ install })
 */
export default GlobalDirectives;
